import apiClient from '../../../api/apiClient';

export const fetchProjects = () =>
  apiClient.get('/api/sample-mgmt/projects/').then((r) => r.data);

export const fetchBatches = (params = {}) =>
  apiClient.get('/api/sample-mgmt/batches/', { params }).then((r) => r.data);

export const fetchBatch = (id) =>
  apiClient.get(`/api/sample-mgmt/batches/${id}/`).then((r) => r.data);

export const updateBatch = (id, data) =>
  apiClient.patch(`/api/sample-mgmt/batches/${id}/`, data).then((r) => r.data);

export const createBatch = (data) =>
  apiClient.post('/api/sample-mgmt/batches/', data).then((r) => r.data);

export const finalizeBatch = (id) =>
  apiClient.post(`/api/sample-mgmt/batches/${id}/finalize/`).then((r) => r.data);

export const reopenBatch = (id) =>
  apiClient.post(`/api/sample-mgmt/batches/${id}/reopen/`).then((r) => r.data);

export const voidBatch = (id) =>
  apiClient.post(`/api/sample-mgmt/batches/${id}/void/`).then((r) => r.data);

export const fetchBatchSamples = (id) =>
  apiClient.get(`/api/sample-mgmt/batches/${id}/samples/`).then((r) => r.data);

export const confirmSampleComposition = (batchId, batchSampleId, composition) =>
  apiClient
    .post(
      `/api/sample-mgmt/batches/${batchId}/samples/${batchSampleId}/composition/confirm/`,
      { composition }
    )
    .then((r) => r.data);

export function getRecentRuns() {
  return apiClient
    .get('/api/sample-mgmt/batches/recent-runs/')
    .then((res) => res.data);
}

export function fetchBatchRunList(batchId) {
  return apiClient
    .get(`/api/sample-mgmt/batches/${batchId}/runs/lightweight/`)
    .then((res) => res.data);
}

export function fetchBatchRunSavedDetail(batchId, runId) {
  return apiClient
    .get(`/api/sample-mgmt/batches/${batchId}/runs/${runId}/saved-detail/`)
    .then((res) => res.data);
}

export function fetchBatchSizeClusters(batchId, options = {}) {
  const params = {};
  if (options.includeSeries) {
    params.include_series = 'true';
  }
  return apiClient
    .get(`/api/sample-mgmt/batches/${batchId}/size-clusters/`, { params })
    .then((res) => res.data);
}

export const fetchAuditEvents = (params = {}) =>
  apiClient.get('/api/audit/events/', { params }).then((r) => r.data);

export const searchBatchSamples = (params = {}) =>
  apiClient
    .get('/api/sample-mgmt/batches/sample-search/', { params })
    .then((r) => r.data);

export const fetchSizeSetupDefaults = () =>
  apiClient.get('/api/size-analysis/setup-defaults/').then((r) => r.data);

export const importBatchSamples = (
  batchId,
  files,
  pipeline,
  instrument,
  experimentalSetupInput,
  cycleNumber
) => {
  const formData = new FormData();
  formData.append('cycle_number', cycleNumber);
  files.forEach((file) => formData.append('files', file));
  if (pipeline) {
    formData.append('pipeline', pipeline);
  }
  if (instrument) {
    formData.append('instrument', instrument);
  }
  if (pipeline === 'size' && experimentalSetupInput) {
    formData.append(
      'experimental_setup_input',
      JSON.stringify(experimentalSetupInput)
    );
  }
  return apiClient
    .post(`/api/sample-mgmt/batches/${batchId}/import/`, formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    })
    .then((r) => r.data);
};

export const fetchBatchAuditTrail = (batchId, limit = 50) =>
  fetchAuditEvents({ batch: batchId }).then((events) =>
    events.slice(0, limit)
  );

export const fetchBatchMeasurements = (batchId) =>
  apiClient.get(`/api/sample-mgmt/batches/${batchId}/measurements/`).then((r) => r.data);

export const excludeMeasurement = (batchId, measurementId, reason = '', scope = {}) =>
  apiClient
    .post(`/api/sample-mgmt/batches/${batchId}/measurements/${measurementId}/exclude/`, { reason, ...scope })
    .then((r) => r.data);

export const includeMeasurement = (batchId, measurementId, scope = {}) =>
  apiClient
    .post(`/api/sample-mgmt/batches/${batchId}/measurements/${measurementId}/include/`, scope)
    .then((r) => r.data);

export const autoGroupBatch = (batchId, pipeline) =>
  apiClient.post(`/api/sample-mgmt/batches/${batchId}/grouping/auto/`, { pipeline }).then((r) => r.data);

export const confirmGrouping = (batchId, clusters, unclusterable = [], scope = {}) =>
  apiClient
    .post(`/api/sample-mgmt/batches/${batchId}/grouping/confirm/`, {
      ...scope,
      clusters,
      ...(unclusterable.length ? { unclusterable } : {}),
    })
    .then((r) => r.data);

export const fetchMeasurementTraces = (batchId, measurementId) =>
  apiClient
    .get(`/api/sample-mgmt/batches/${batchId}/measurements/${measurementId}/traces/`)
    .then((r) => r.data);

export const selectMeasurementTrace = (batchId, measurementId, payload) =>
  apiClient
    .post(`/api/sample-mgmt/batches/${batchId}/measurements/${measurementId}/select_trace/`, payload)
    .then((r) => r.data);

export const fetchMeasurementPreview = (batchId, measurementId, traceIdentity = null) => {
  const params = traceIdentity
    ? {
        trace_id: traceIdentity.trace_id ?? '',
        wavelength_nm: traceIdentity.wavelength_nm ?? '',
      }
    : undefined;
  return apiClient
    .get(
      `/api/sample-mgmt/batches/${batchId}/measurements/${measurementId}/preview/`,
      params ? { params } : undefined
    )
    .then((r) => r.data);
};

export function buildInitialTraceSelections(measurements, pipelines) {
  if (!Array.isArray(pipelines) || !pipelines.includes('size')) return [];

  return (Array.isArray(measurements) ? measurements : [])
    .filter((measurement) => {
      const role = String(measurement?.file_role || 'raw')
        .trim()
        .toLowerCase()
        .replace(/-/g, '_');
      const hasExplicitIdentity =
        Boolean(String(measurement?.selected_trace_id || '').trim()) ||
        measurement?.selected_wavelength_nm != null;
      return (
        measurement?.derived_status === 'ready' &&
        !measurement?.is_excluded &&
        ['raw', 'size_input'].includes(role) &&
        hasExplicitIdentity
      );
    })
    .map((measurement) => ({
      import_id: Number(measurement.id),
      trace_id: String(measurement.selected_trace_id || '').trim() || null,
      wavelength_nm: measurement.selected_wavelength_nm ?? null,
    }))
    .filter((selection) => Number.isInteger(selection.import_id));
}

export const runBatchAnalysis = (batchId, payload) =>
  apiClient.post(`/api/sample-mgmt/batches/${batchId}/run/`, payload).then((r) => r.data);

// Keep HTTP status: 200 is a replay, 202 is a newly accepted initial request.
export const submitInitialRun = (batchId, payload) =>
  apiClient.post(`/api/sample-mgmt/batches/${batchId}/run/`, payload)
    .then(({ status, data }) => ({ status, data }));

export const previewAnalysisCost = (batchId, scope) =>
  apiClient.post('/api/credits/analysis-cost-preview/', {
    batch_id: batchId,
    ...scope,
  }).then((r) => r.data);

export const previewDerivedRun = (batchId, payload) =>
  apiClient
    .post(`/api/sample-mgmt/batches/${batchId}/exploratory-run-preview/`, payload)
    .then((r) => r.data);

export const saveDerivedRun = (batchId, payload) =>
  apiClient
    .post(`/api/sample-mgmt/batches/${batchId}/derived-run/`, payload)
    .then((r) => r.data);

export const fetchInitialRunCreditBalance = (organization) =>
  apiClient.get('/api/credits/balance/', { params: { organization } }).then((r) => r.data);
